import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import { formatDay } from '../dates'
import { parseAmount } from '../amounts'
import { kpisFor, type Kpi } from '../kpis'
import { useTrade } from '../trade'
import { FigureChart, deltaLabel, toneOf, type Tone } from '../components/FigureChart'
import { Fold } from '../components/Fold'

/*
  The Numbers.

  One place where the board and the owner read the same figures. Everything on this screen
  came from somewhere: a statement that was uploaded, or a number typed into the company
  profile. The source is always shown, because a figure nobody can trace is a figure nobody
  can argue with.

  Nothing here is reconciled quietly. When two sources disagree by more than a percent the
  screen says so at the top and refuses to pick, because the alternative is an owner
  running his business on a number he never chose.
*/

type Unit = 'usd' | 'count' | 'percent'

/** What kind of stretch of time a figure covers. A year and eight months of one are not
    the same species of number, and the screen never lets them look like it. */
type Shape = 'FullYear' | 'YearToDate' | 'Quarter' | 'Month' | 'Partial' | 'Undated'

interface FigureSource {
  value: number
  label: string
  kind: string
  kindLabel: string
  documentId: string | null
  note: string | null
  createdAt: string
  periodLabel: string
  periodEnd: string | null
  dismissed: boolean
  /** An earlier reading of a running figure. True when it was taken, overtaken since. */
  superseded: boolean
}

interface Figure {
  metric: string
  metricLabel: string
  periodLabel: string
  periodStart: string | null
  periodEnd: string | null
  shape: Shape
  shapeLabel: string
  /** Covers part of its period rather than all of it. Year to date, and nothing else yet. */
  partial: boolean
  value: number
  unit: Unit
  conflicted: boolean
  settled: boolean
  /** Which way is good. Null where nobody has said, which is most of them. */
  higherIsBetter: boolean | null
  /** "you settled this" or "you corrected this". Null until the owner has ruled. */
  decidedNote: string | null
  key: string
  profileBacked: boolean
  spreadPct: number
  low: number
  high: number
  sources: FigureSource[]
}

interface Numbers {
  fiscalYearEnd: string
  lastCompleteLabel: string
  currentStart: string
  currentEnd: string
  /** How far through the year in progress today is, as a whole percent. */
  currentElapsedPct: number
  lastComplete: Figure[]
  current: Figure[]
  other: Figure[]
  conflicts: Figure[]
}

export function TheNumbers() {
  const [data, setData] = useState<Numbers | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = async () => setData(await api.get<Numbers>('/api/numbers'))

  useEffect(() => { void load().catch(() => setData(null)) }, [])

  /*
    Settling and undoing both reload rather than patching state locally. Resolving a
    revenue figure can rewrite the company profile, which regenerates the owner's claims
    on the next read, and a screen that guessed the outcome would show something the
    server never agreed to.
  */
  const settle = async (key: string, value: number) => {
    setBusy(key)
    try {
      await api.post('/api/numbers/settle', { key, value })
      await load()
    } finally { setBusy(null) }
  }

  const unsettle = async (key: string) => {
    setBusy(key)
    try {
      await api.post('/api/numbers/unsettle', { key })
      await load()
    } finally { setBusy(null) }
  }

  if (!data) return <div className="skeleton" style={{ width: '55%' }} />

  const total = data.lastComplete.length + data.current.length + data.other.length

  // One list, so a card in any section can find the same metric in the others. The trend is
  // across periods, and the sections are a way of arranging periods rather than a boundary.
  const everything = [...data.lastComplete, ...data.current, ...data.other]

  return (
    <section className="numbers">
      <header className="screen-head">
        <h1 className="screen-title">The Numbers</h1>
        <p className="screen-sub">
          {total === 0
            ? 'Nothing to show yet.'
            : 'What this business has stated about itself, and where it agrees with itself.'}
        </p>
      </header>

      {data.conflicts.length > 0 && (
        <div className="conflict-banner" role="alert">
          <span className="conflict-banner__mark" aria-hidden><IconWarn /></span>
          <div>
            <p className="conflict-banner__title">
              {data.conflicts.length === 1
                ? 'Your numbers disagree on one figure'
                : `Your numbers disagree on ${data.conflicts.length} figures`}
            </p>
            <p className="conflict-banner__body">
              Two sources report different values for the same period. The board has been
              told not to pick one, so until this is settled its advice on{' '}
              {list(data.conflicts.map(c => c.metricLabel.toLowerCase()))} comes with a
              caveat attached. You know which is right. Say so and it stops asking.
            </p>
            <ul className="conflict-banner__list">
              {data.conflicts.map(c => (
                <li key={c.key}>
                  <span className="conflict-banner__what">
                    <strong>{c.metricLabel}, {c.periodLabel}</strong>
                    {' — a gap of '}{c.spreadPct}%
                  </span>
                  <span className="conflict-banner__pick">
                    {/* The claims about the same moment, and nothing else. Offering March's
                        reading as a way to settle a disagreement about September would have
                        the owner set the year to date back six months. */}
                    {c.sources.filter(s => !s.dismissed && !s.superseded).map((s, i) => (
                      <button
                        key={i}
                        className="pick"
                        disabled={busy === c.key}
                        onClick={() => void settle(c.key, s.value)}
                      >
                        {money(s.value, c.unit)}
                        <span className="pick__from">{s.kindLabel}</span>
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <WhatToSend data={data} total={total} onSaved={load} />

      <Section
        title={data.lastCompleteLabel}
        sub="The last complete year"
        figures={data.lastComplete}
        all={everything} elapsedPct={data.currentElapsedPct}
        open={open} setOpen={setOpen}
        busy={busy} settle={settle} unsettle={unsettle}
      />

      {/* The elapsed share is the whole point of the heading. Without it an owner reads
          310,000 against last year's 560,487 and sees a business half its size, when what
          he is looking at is eight months of one. */}
      <Section
        title="This year to date"
        sub={`Since ${date(data.currentStart)} · ${data.currentElapsedPct}% of the year gone`}
        figures={data.current}
        showPeriod
        all={everything} elapsedPct={data.currentElapsedPct}
        open={open} setOpen={setOpen}
        busy={busy} settle={settle} unsettle={unsettle}
      />

      <Section
        title="Other periods"
        sub="Quarters, months, older years, and anything undated"
        figures={data.other}
        showPeriod
        all={everything} elapsedPct={data.currentElapsedPct}
        open={open} setOpen={setOpen}
        busy={busy} settle={settle} unsettle={unsettle}
      />
    </section>
  )
}

function Section({
  title, sub, figures, all, elapsedPct, showPeriod, open, setOpen, busy, settle, unsettle,
}: {
  title: string
  sub: string
  figures: Figure[]
  /** Every figure on the screen, so a card can find its own metric in other periods. */
  all: Figure[]
  /** How far through the fiscal year today is, for the year-to-date cards. */
  elapsedPct: number
  /** Print each figure's own period on the card face. Sections holding one kind of period
      say it in the heading; these hold several, and two cards both reading "Revenue" with
      nothing to tell them apart is the failure this exists to stop. */
  showPeriod?: boolean
  open: string | null
  setOpen: (k: string | null) => void
  busy: string | null
  settle: (key: string, value: number) => Promise<void>
  unsettle: (key: string) => Promise<void>
}) {
  if (figures.length === 0) return null

  /*
    The same metric in other periods, oldest first, for the sparkline.

    Only dated periods, and only where the shapes match. A quarter next to a full year on one
    line is the mistake FiscalCalendar.ShapeOf exists to stop, and drawing them as consecutive
    points would make Q2 look like a collapse from last year rather than a different species
    of number. Undated figures are dropped for the same reason: they cannot be ordered.
  */
  const history = (f: Figure): { periodEnd: string | null; periodLabel: string; value: number }[] =>
    all
      .filter(x => x.metric === f.metric && x.shape === f.shape && x.periodEnd !== null)
      .sort((a, b) => (a.periodEnd! < b.periodEnd! ? -1 : 1))
      .map(x => ({ periodEnd: x.periodEnd, periodLabel: x.periodLabel, value: x.value }))

  /** The move against the period before, or null where there is nothing to move from. */
  const delta = (
    points: { value: number }[], f: Figure,
  ): { tone: Tone; arrow: string; label: string } | null => {
    if (points.length < 2) return null

    const from = points[points.length - 2]!.value
    const to = points[points.length - 1]!.value
    if (to === from) return null

    return {
      tone: toneOf(from, to, f.higherIsBetter),
      arrow: to > from ? '↑' : '↓',
      label: deltaLabel(from, to, f.unit),
    }
  }

  return (
    <div className="numsection">
      <div className="numsection__head">
        <h2 className="numsection__title">{title}</h2>
        <span className="numsection__sub">{sub}</span>
      </div>

      <ul className="numgrid">
        {figures.map(f => {
          const key = f.key
          const isOpen = open === key

          // Computed once: the trend chart and the arrow beside the number read the same
          // series, and calling for it twice invites them to disagree.
          const points = history(f)
          const move = delta(points, f)

          return (
            <li
              key={key}
              className={`num${f.conflicted ? ' num--conflict' : ''}${f.settled ? ' num--settled' : ''}`}
            >
              <button
                className="num__main"
                onClick={() => setOpen(isOpen ? null : key)}
                aria-expanded={isOpen}
              >
                <span className="num__label">
                  {f.metricLabel}
                  {showPeriod && <span className="num__chip">{f.periodLabel}</span>}
                </span>

                <span className="num__value">
                  {f.conflicted
                    ? <>{money(f.low, f.unit)} <span className="num__vs">or</span> {money(f.high, f.unit)}</>
                    : money(f.value, f.unit)}

                  {/*
                    The move since the last period of the same shape.

                    Same rule the Home tile follows: no prior period of the same shape, no
                    arrow. Comparing eight months of this year against all of last year is
                    the one comparison this screen exists to refuse, and an arrow is a
                    comparison whether or not it admits to being one.
                  */}
                  {move && (
                    <span className={`num__delta num__delta--${move.tone}`}>
                      {move.arrow} {move.label}
                    </span>
                  )}
                </span>

                {/*
                  The picture, where there is one worth drawing. FigureChart decides which
                  from what this box has to compare against, and returns nothing at all for
                  a lone dollar figure with one source, because there is nothing to draw.
                */}
                <FigureChart
                  unit={f.unit}
                  value={f.value}
                  low={f.low}
                  high={f.high}
                  sources={f.sources}
                  history={points}
                  elapsedPct={elapsedPct}
                  partial={f.partial}
                  higherIsBetter={f.higherIsBetter}
                  conflicted={f.conflicted}
                />

                <span className="num__foot">
                  {f.conflicted
                    ? <span className="num__flag">Sources differ by {f.spreadPct}% · tap to settle</span>
                    : sourceLine(f)}
                </span>

                {/* A running figure with no date on it is unreadable: two months and eleven
                    look identical. This is the one line that makes it a number rather than
                    a rumour. */}
                {f.partial && f.periodEnd && (
                  <span className="num__asat">As at {date(f.periodEnd)}</span>
                )}
              </button>

              {isOpen && (
                <div className="num__detail">
                  <p className="num__period">{f.periodLabel}</p>

                  {f.conflicted && (
                    <p className="num__ask">
                      Which of these is right? The one you pick becomes the figure the board
                      works from{f.profileBacked ? ', and your company profile is corrected to match' : ''}.
                    </p>
                  )}

                  {/* Only the claims about the same moment. An earlier reading of a
                      running figure is not one of the answers to choose between: it was
                      right in March and nobody is disputing it. */}
                  <ul className="num__sources">
                    {f.sources.filter(s => !s.superseded).map((s, i) => (
                      <li key={i} className={s.dismissed ? 'is-dismissed' : undefined}>
                        <span className="num__srcval">{money(s.value, f.unit)}</span>
                        <span className="num__srcfrom">
                          {s.label ? `"${s.label}" from ` : ''}{s.kindLabel}
                          {s.note ? ` · ${s.note}` : ''}
                          {s.dismissed ? ' · you ruled against this' : ''}
                        </span>
                        <span className="num__srcactions">
                          {s.documentId && (
                            <a
                              className="num__srclink"
                              href={`/api/documents/${s.documentId}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open
                            </a>
                          )}
                          {f.conflicted && !s.dismissed && (
                            <button
                              className="btn btn--small"
                              disabled={busy === f.key}
                              onClick={() => void settle(f.key, s.value)}
                            >
                              Use this one
                            </button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Earlier f={f} />

                  <TypeIt f={f} busy={busy} settle={settle} />

                  {f.settled && (
                    <p className="num__settled">
                      {f.decidedNote === 'you corrected this' ? 'Corrected by you.' : 'Settled by you.'}
                      <button
                        className="linkish"
                        disabled={busy === f.key}
                        onClick={() => void unsettle(f.key)}
                      >
                        Undo
                      </button>
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/*
  Where the figure has been.

  A running figure is restated rather than replaced, so the readings before the current one
  are the only trajectory this screen has. An owner who typed revenue in March, June and
  August can see that it went 140, 255, 310 without opening anything, and that shape is
  usually more use than the latest number on its own.

  Not a disagreement and never presented as one. Nothing here has a "use this one" button,
  because none of it is in contention.
*/
function Earlier({ f }: { f: Figure }) {
  const earlier = f.sources
    .filter(s => s.superseded && s.periodEnd)
    .sort((a, b) => (a.periodEnd! < b.periodEnd! ? 1 : -1))

  if (earlier.length === 0) return null

  /*
    Folded, and closed.

    A running figure restated monthly carries every earlier reading with it, so by the
    fourth quarter a single revenue card can list eight superseded numbers under the one
    that is true. They are worth keeping and worth showing on request: this is the screen
    that reconciles what everybody has claimed. They are not worth putting between the
    owner and the current figure eight times over.
  */
  return (
    <div className="num__earlier">
      <Fold
        title="Earlier this period"
        meta={`${earlier.length} ${earlier.length === 1 ? 'reading' : 'readings'}`}
      >
        <ul>
          {earlier.map((s, i) => (
            <li key={i}>
              <span className="num__srcval">{money(s.value, f.unit)}</span>
              <span className="num__srcfrom">as at {date(s.periodEnd!)} · {s.kindLabel}</span>
            </li>
          ))}
        </ul>
      </Fold>
    </div>
  )
}

/*
  Typing the right figure in.

  Two jobs, one form. Where sources disagree, neither one is right often enough to matter:
  a statement can be for the wrong period and a profile can be a year stale, and an owner
  forced to choose between two wrong numbers will pick one and stop trusting the screen.
  Where they agree, they can still all be wrong together, which is what happens when a
  number was misread off a scan or a percentage was transcribed off the wrong column.

  This screen is the board's final word on every figure, so it has to be the place the
  figure can be fixed. It writes a correction rather than editing the claim underneath:
  the statement still says what it says, and undo brings the old reading back.
*/
function TypeIt({
  f, busy, settle,
}: {
  f: Figure
  busy: string | null
  settle: (key: string, value: number) => Promise<void>
}) {
  const [typed, setTyped] = useState('')
  const parsed = parseAmount(typed)
  const ready = parsed !== null && Number.isFinite(parsed)

  const placeholder = f.unit === 'count' ? '12' : f.unit === 'percent' ? '31.4' : '560,488'

  return (
    <form
      className="num__type"
      onSubmit={e => { e.preventDefault(); if (ready) void settle(f.key, parsed as number) }}
    >
      <label className="num__typelabel" htmlFor={`t-${f.key}`}>
        {f.conflicted
          ? 'Neither? Type the right figure.'
          : f.settled
            ? 'Still wrong? Type it again.'
            : 'Wrong? Type the right figure.'}
      </label>
      <div className="num__typerow">
        {f.unit === 'usd' && <span className="num__typeaffix">$</span>}
        <input
          id={`t-${f.key}`}
          className="input"
          inputMode="decimal"
          placeholder={placeholder}
          value={typed}
          onChange={e => setTyped(e.target.value)}
        />
        {f.unit === 'percent' && <span className="num__typeaffix">%</span>}
        <button className="btn btn--small" disabled={!ready || busy === f.key}>
          {f.conflicted ? 'Use that' : 'Correct it'}
        </button>
      </div>
    </form>
  )
}

/* One source needs no explanation; several agreeing is worth saying out loud.
   Earlier readings of a running figure are not sources agreeing with anything: counting
   them made a business that reported monthly read as "9 sources agree". */
function sourceLine(f: Figure): string {
  const live = f.sources.filter(s => !s.dismissed && !s.superseded)
  // "Settled by you" under a figure nobody disagreed about reads as the app inventing an
  // argument. The server says which act this was.
  if (f.settled) return f.decidedNote === 'you corrected this' ? 'Corrected by you' : 'Settled by you'
  if (live.length === 1) return `From ${live[0].kindLabel}`
  return `${live.length} sources agree`
}

function money(value: number, unit: Unit): string {
  if (unit === 'count') return value.toLocaleString()
  // A ratio, printed as one. Trade KPIs put percentages on this screen for the first time,
  // and a food cost of 31.4 rendered as $31 is not a rounding problem, it is a wrong number.
  if (unit === 'percent') return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
  return value.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  })
}

/* A period boundary is a calendar day, not an instant. new Date("2026-09-04") is midnight
   UTC and renders as the third of September west of Greenwich, which is how every date on
   this screen used to come out a day early. */
function date(iso: string): string {
  return formatDay(iso, { month: 'long', day: 'numeric', year: 'numeric' })
}

/* "revenue and payroll", not "revenue, payroll". */
function list(items: string[]): string {
  const unique = [...new Set(items)]
  if (unique.length === 1) return unique[0]
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`
  return `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`
}

function IconWarn() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

/*
  What this particular business should be sending.

  The old empty state said "upload a statement and the figures land here", which does not
  tell an owner which figures matter. A restaurant that never puts prime cost in front of
  its board is asking it to reason about a restaurant with its eyes shut.

  Shown whether or not anything has arrived. Once figures exist it becomes the more useful
  half of the screen: the list of what is still missing.
*/
function WhatToSend(
  { data, total, onSaved }: { data: Numbers; total: number; onSaved: () => void },
) {
  const trade = useTrade()

  const { label, rows } = useMemo(() => {
    const set = kpisFor(trade)
    const figures = [...data.lastComplete, ...data.current, ...data.other]

    /*
      The canonical key first, which is exact: anything typed on this screen is stored under
      kpi.metric, so that comparison can never miss. The pattern is the fallback, for figures
      read off a statement under whatever the accountant called the line.
    */
    const has = (kpi: Kpi) =>
      figures.some(f =>
        f.metric === kpi.metric || kpi.match.test(f.metric) || kpi.match.test(f.metricLabel))

    return { label: set.label, rows: set.kpis.map(k => ({ kpi: k, on: has(k) })) }
  }, [trade, data])

  // Whichever year the owner is working through, for every row at once. Set on the card
  // rather than per row because somebody filling gaps is filling them from one statement,
  // and asking the question again on every line is the form this was built to avoid.
  const [period, setPeriod] = useState<'ytd' | 'last_year'>('ytd')

  const missing = rows.filter(r => !r.on)
  if (missing.length === 0) return null

  /*
    `card`, and deliberately not `card kpi`. `.kpi` is the Home masthead's metric tile:
    an inline-flex, flex:none, white-space:nowrap row. Wearing it laid this card's title,
    lede, period chips and checklist out side by side on one baseline and pushed them off
    the right of their own border. Below 560px it did the opposite and hid the card
    outright, because that breakpoint also carries `.kpi:nth-child(n+3) { display: none }`
    and the card is the third child of `.numbers` whenever a conflict banner is above it.

    Nothing on this screen wants a bare `.kpi`; only the `kpi__*` children, which do not
    collide. Do not add a block class back without checking app.css for the name first.
  */
  return (
    <div className="card">
      <div className="card__title">What {label ? `a ${label} board` : 'the board'} asks for</div>
      <p className="muted kpi__lede">
        {total === 0
          ? 'Upload a statement from The Table or the Company screen and its figures land here. These are the ones worth making sure it contains.'
          : `${missing.length} of these ${missing.length === 1 ? 'is' : 'are'} not on file yet. The board reasons without them rather than asking twice, so it is worth filling the gaps.`}
      </p>

      {/* Two periods and no date picker. These are the two an owner holds in his head:
          where the business is now, and what last year came to. The server dates both, so
          nothing typed here can be aimed at a year it does not belong to. */}
      <div className="kpi__period" role="group" aria-label="Which period these figures are for">
        <button
          type="button"
          className={period === 'ytd' ? 'chipbtn is-on' : 'chipbtn'}
          aria-pressed={period === 'ytd'}
          onClick={() => setPeriod('ytd')}
        >
          This year to date
        </button>
        <button
          type="button"
          className={period === 'last_year' ? 'chipbtn is-on' : 'chipbtn'}
          aria-pressed={period === 'last_year'}
          onClick={() => setPeriod('last_year')}
        >
          {data.lastCompleteLabel}
        </button>
      </div>

      <ul className="kpi__list">
        {rows.map(({ kpi, on }) => (
          <li key={kpi.name} className={on ? 'kpi__row is-on' : 'kpi__row'}>
            <span className="kpi__mark" aria-hidden>{on ? '✓' : ''}</span>
            <span>
              <span className="kpi__name">{kpi.name}</span>
              <span className="kpi__why">{kpi.why}</span>
            </span>
            {on
              ? <span className="kpi__state">on file</span>
              : <QuickAdd kpi={kpi} period={period} onSaved={onSaved} />}
          </li>
        ))}
      </ul>
    </div>
  )
}

/*
  Typing one of the listed numbers in, without leaving the list.

  Listing what a board needs and then offering nowhere to put it is the reason most of these
  never arrive: the owner knows their food cost, and the cost of telling us was a trip to a
  screen that has no field for it. The field is right on the row, one number, no period
  picker and no form.

  The period is still dated by the server, and the choice above the list is between the two
  it knows how to date. A real period selector on every row would turn a two-second answer
  back into a form, which is what kept these figures off the screen in the first place.
*/
function QuickAdd({
  kpi, period, onSaved,
}: {
  kpi: Kpi
  period: 'ytd' | 'last_year'
  onSaved: () => void
}) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    // Tolerant of what people actually type: 34%, $12,400, 1,166, 42k.
    const n = parseAmount(value)
    if (n === null || Number.isNaN(n)) { setError('That is not a number.'); return }

    setBusy(true)
    setError(null)
    try {
      await api.post('/api/numbers', {
        metric: kpi.metric, label: kpi.name, value: n, unit: kpi.unit, period,
      })
      setValue('')
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="quick" onSubmit={save}>
      <span className="quick__field">
        {kpi.unit === 'usd' && <span className="quick__affix">$</span>}
        <input
          className="input quick__input"
          inputMode="decimal"
          placeholder={kpi.unit === 'percent' ? '34' : kpi.unit === 'count' ? '1,166' : '12,400'}
          value={value}
          onChange={e => { setValue(e.target.value); setError(null) }}
          aria-label={kpi.name}
        />
        {kpi.unit === 'percent' && <span className="quick__affix">%</span>}
      </span>
      <button className="btn btn--sm" disabled={busy || !value.trim()}>
        {busy ? '...' : 'Add'}
      </button>
      {error && <span className="quick__error">{error}</span>}
    </form>
  )
}
